import { useContext, useEffect, useState } from 'react';
import AudioPlayer from 'react-h5-audio-player';
import { useTranslation } from 'react-i18next';

import clsx from 'clsx';

import { Modal, SingleSelect } from '@/common';
import { Spinner } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { queryClient } from '@/configuration/react-query';
import { STORAGE_LIMIT_EXCEEDED_ERROR_CODE, TIME_LIMIT_EXCEEDED_ERROR_CODE } from '@/constants';
import { AuthContext } from '@/contexts';
import { ORGANIZATION_PERMISSION_TYPE } from '@/enums/OrganizationPermissionEnum';
import { getPermissionsForOrganization } from '@/helpers';
import { showToast, ToastType } from '@/helpers/toast';
import { PauseIcon, PlayIcon } from '@/resources';
import { useGetOrganizationByIdSimplifiedQuery } from '@/services';
import { useDownloadSongMutation, usePreviewMusicQuery } from '@/services/music.service';
import { DownloadSong, SelectOptionProps } from '@/types';

import { SearchMusicResults } from './SearchMusicResults';

type SearchMusicModalProps = {
  isOpen: boolean;
  onClose: () => void;
  organizationId: number | undefined;
  onSave: () => void;
  organizationOptions?: SelectOptionProps[];
};

export const SearchMusicModal = ({
  isOpen,
  onClose,
  organizationId,
  onSave,
  organizationOptions = []
}: SearchMusicModalProps) => {
  const [t] = useTranslation('common');

  const [songUrl, setSongUrl] = useState<string | undefined>('');
  const [selectedSongs, setSelectedSongs] = useState<DownloadSong[]>([]);
  const [previewSongId, setPreviewSongId] = useState<number | undefined>();
  const [previewSongName, setPreviewSongName] = useState<string | undefined>();
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [organizationValue, setOrganizationValue] = useState<SelectOptionProps | null>(null);

  const effectiveOrganizationId = organizationId ?? organizationValue?.value;

  const { organizationPermissions: userOrganizationPermissions } = useContext(AuthContext);
  const { data: selectedOrganization, isFetching: isFetchingSelectedOrganization } =
    useGetOrganizationByIdSimplifiedQuery(Number(effectiveOrganizationId ?? 0));
  const selectedOrganizationPermissions = getPermissionsForOrganization(
    userOrganizationPermissions,
    selectedOrganization?.data
  );
  const hasMusicProviderAccess = selectedOrganizationPermissions.includes(
    ORGANIZATION_PERMISSION_TYPE.MUSIC_PROVIDER
  );

  const hasMediaLibraryAccess =
    !isFetchingSelectedOrganization &&
    selectedOrganizationPermissions.includes(ORGANIZATION_PERMISSION_TYPE.MEDIA_LIBRARY);
  const mediaLibraryMissingMessage =
    effectiveOrganizationId && !isFetchingSelectedOrganization && !hasMediaLibraryAccess
      ? t('yourOranizationDoesntHavePermission', {
          permission: t(ORGANIZATION_PERMISSION_TYPE.MEDIA_LIBRARY)
        })
      : undefined;

  const { mutateAsync: downloadSong } = useDownloadSongMutation(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('downloadSongSuccess'));
    },
    onError: (error) => {
      const backendError = error.response?.data?.ErrorMessages?.[0];
      const message = backendError?.DisplayMessage ?? t('downloadSongError');
      const errorCode = backendError?.Code;
      const isLimitExceeded =
        !!errorCode &&
        [STORAGE_LIMIT_EXCEEDED_ERROR_CODE, TIME_LIMIT_EXCEEDED_ERROR_CODE].includes(errorCode);

      if (isLimitExceeded) {
        showToast(ToastType.WARNING, {
          text: message,
          autoClose: false
        });
        return;
      }

      showToast(ToastType.ERROR, message);
    }
  });

  const { data: previewMusic, refetch: refetchPreview } = usePreviewMusicQuery(
    Number(previewSongId)
  );

  useEffect(() => {
    if (previewSongId !== undefined && !isNaN(previewSongId)) {
      refetchPreview();
    }
  }, [previewSongId]);

  useEffect(() => {
    if (previewMusic?.data) {
      setSongUrl(previewMusic.data);
    }
  }, [previewMusic]);

  const handleSave = async () => {
    setIsDownloadingAll(true);
    for (const song of selectedSongs) {
      const payload: DownloadSong = {
        id: song.id,
        title: song.title,
        artist: song.artist,
        duration: song.duration,
        organizationId: Number(effectiveOrganizationId)
      };
      await downloadSong({ payload });
    }
    setIsDownloadingAll(false);
    onSave();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      height='h-full'
      label={t('searchForMusic')}
      hasButton
      hasCancelButton
      buttonText={t('save')}
      buttonClasses='flex justify-end'
      buttonType={ButtonVariant.PRIMARY}
      isButtonDisabled={
        selectedSongs.length === 0 ||
        isDownloadingAll ||
        (!organizationId && !organizationValue) ||
        !hasMusicProviderAccess ||
        !hasMediaLibraryAccess
      }
      modalClasses='w-full md:w-[600px] border border-secondary-blue-gray'
      handleSubmit={handleSave}>
      <div className='w-full border-b' />
      {!organizationId && !isDownloadingAll && (
        <div className='my-4 w-full px-2'>
          <SingleSelect
            id='org-select-music-modal'
            options={organizationOptions}
            value={organizationValue}
            handleChange={setOrganizationValue}
            isSearchable={true}
            isClearable={true}
            isRequired={true}
            label={t('organization')}
            placeholder={t('organization')}
            className='w-full'
            errorMessage={mediaLibraryMissingMessage}
            isInvalid={!!mediaLibraryMissingMessage}
          />
        </div>
      )}
      <div className='w-full px-2'>
        <AudioPlayer
          customIcons={{ play: <PlayIcon />, pause: <PauseIcon /> }}
          header={<p className='mt-2 text-sm font-semibold'>{previewSongName}</p>}
          autoPlay={false}
          src={songUrl}
          className={clsx('mt-4 w-full', !songUrl && 'opacity-50')}
        />
      </div>

      <div className='relative my-5 min-h-[200px] w-full px-2'>
        {isDownloadingAll && (
          <div className='absolute left-0 top-0 z-50 flex size-full items-center justify-center bg-white'>
            <Spinner />
          </div>
        )}
        {!isDownloadingAll && (
          <SearchMusicResults
            selectedSongs={selectedSongs}
            setSelectedSongs={setSelectedSongs}
            previewSongId={previewSongId}
            setPreviewSongId={setPreviewSongId}
            setPreviewSongName={setPreviewSongName}
            hasMusicProviderAccess={hasMusicProviderAccess}
          />
        )}
      </div>
    </Modal>
  );
};
